// SPDX-License-Identifier: MIT
/**
 * ui-steps (how it works, 6–10 beats). Two to four steps on one line: a card per step rises in, its
 * marker lights, a small piece of interface does the step (text is typed, a switch flips, an upload
 * fills, items are checked off, a link is made) and the marker turns into a check on the beat. A
 * line draws from marker to marker as the steps complete, so the film reads "one, two, three".
 *
 * Titles and texts are claims and must come from the brand pack. Ordinals are drawn by the scene;
 * they count steps, they are not figures.
 */
import * as z from "zod/mini";
import { clamp } from '../core/math.ts';
import * as kit from '../kit/index.ts';
import type { Box } from '../kit/path.ts';
import { DESIGN_H, DESIGN_W, type SceneEnv, type SceneTiming, defineScene } from '../scene.ts';
import { sceneBeats } from './lib.ts';
const { ui } = kit;
const LEFT = 150;
const RIGHT = DESIGN_W - 150;
const GAP = 40;
const CARD_Y = 370;
const CARD_H = 450;
const MARK_Y = 270;
const TITLE_PX = 40;
const ACTIONS = ['type', 'toggle', 'upload', 'check', 'link'] as const;
type Action = (typeof ACTIONS)[number];
const ICON: Record<Action, kit.ui.IconName> = {
    type: 'search',
    toggle: 'sliders',
    upload: 'upload',
    check: 'check',
    link: 'link',
};
const propsSchema = z.object({
    steps: z
        .array(z.object({
        title: z.string().check(z.maxLength(36)),
        action: z.enum(ACTIONS),
        /** What a `type` step types, or a line shown under the title. */
        text: z.optional(z.string().check(z.maxLength(32))),
    })).check(z.minLength(2)).check(z.maxLength(4)),
});
type Step = z.infer<typeof propsSchema>['steps'][number];
/** Beat at which step i completes: evenly spread, on whole beats where the length allows. */
function doneAt(i: number, n: number, { beats }: Pick<SceneTiming, 'beats'>): number {
    const first = 1.5;
    const last = beats - 1.5;
    const b = n === 1 ? last : first + ((last - first) * (i + 1)) / n;
    return Math.max(first, Math.round(b * 2) / 2);
}
/** Each step starts its action this long before it completes. */
const WORK = 1.1;
function card(i: number, n: number): Box {
    const w = (RIGHT - LEFT - GAP * (n - 1)) / n;
    return { x: LEFT + i * (w + GAP), y: CARD_Y, w, h: CARD_H };
}
/** The small piece of interface that does the step, in the lower part of its card. */
function action(ctx: CanvasRenderingContext2D, tk: kit.ui.UiTokens, env: SceneEnv, s: Step, area: Box, t0: number, t1: number): void {
    const k = (a: number, b: number, ease = kit.curves.glide) => kit.beat.seg(env, t0 + (t1 - t0) * a, t0 + (t1 - t0) * b, ease);
    const cy = area.y + area.h / 2;
    switch (s.action) {
        case 'type': {
            const h = 72;
            const box = { x: area.x, y: cy - h / 2, w: area.w, h };
            const ty = s.text ? ui.typing(env, s.text, t0, t0 + (t1 - t0) * 0.85, 5) : null;
            ui.field(ctx, tk, box, {
                text: ty?.text ?? '',
                focus: k(0, 0.1),
                caret: (ty?.caret ?? 1) * k(0, 0.1) * (1 - k(0.95, 1)),
                icon: 'search',
            });
            if (!s.text)
                ui.skeleton(ctx, tk, box.x + 84, cy - 7, (box.w - 130) * k(0, 0.9), 14);
            break;
        }
        case 'toggle': {
            for (let r = 0; r < 2; r++) {
                const y = cy - 44 + r * 88;
                ui.skeleton(ctx, tk, area.x, y - 7, area.w * (0.5 - r * 0.12), 14);
                ui.toggle(ctx, tk, area.x + area.w - 86, y, 48, r === 0 ? kit.curves.spring(k(0.35, 0.8, (x) => x)) : 0);
            }
            break;
        }
        case 'upload': {
            const p = k(0.05, 0.85, kit.curves.standard);
            ui.panel(ctx, tk, { x: area.x, y: cy - 70, w: area.w, h: 140 }, { fill: tk.sunken, lift: 0 });
            ui.icon(ctx, p >= 1 ? 'check' : 'upload', area.x + 28, cy - 46, 40, tk.accentText, p >= 1 ? k(0.85, 1) : 1);
            ui.skeleton(ctx, tk, area.x + 88, cy - 34, area.w * 0.45, 14);
            ui.progress(ctx, tk, { x: area.x + 28, y: cy + 20, w: area.w - 56, h: 12 }, p);
            break;
        }
        case 'check': {
            for (let r = 0; r < 3; r++)
                ui.row(ctx, tk, { x: area.x - 12, y: cy - 132 + r * 88, w: area.w + 24, h: 80 }, {
                    avatar: false,
                    icon: 'check',
                    bars: [0.6 - 0.1 * r, 0.32],
                    check: k(0.2 + r * 0.25, 0.4 + r * 0.25),
                });
            break;
        }
        case 'link': {
            const pop = kit.curves.spring(k(0.3, 0.8, (x) => x));
            ui.panel(ctx, tk, { x: area.x, y: cy - 44, w: area.w, h: 88 }, { fill: tk.sunken, lift: 0, r: 44 });
            ui.icon(ctx, 'link', area.x + 28, cy - 18, 36, tk.accentText, clamp(pop * 2));
            ui.skeleton(ctx, tk, area.x + 80, cy - 7, (area.w - 200) * clamp(pop * 1.4), 14);
            if (pop > 0) {
                const r = 22 * pop;
                ctx.beginPath();
                ctx.arc(area.x + area.w - 44, cy, r, 0, Math.PI * 2);
                ctx.fillStyle = tk.accent;
                ctx.fill();
                ui.icon(ctx, 'check', area.x + area.w - 58, cy - 14, 28, tk.onAccent, clamp((pop - 0.5) * 2));
            }
            break;
        }
    }
}
export default defineScene({
    id: 'ui-steps',
    version: '1.0.0',
    aspects: ['16:9'],
    propsSchema,
    glyphs: (p) => p.steps.map((s) => s.title + (s.text ?? '')).join('') + '1234',
    claims: (p) => p.steps.flatMap((s) => [s.title, ...(s.text ? [s.text] : [])]),
    motion: () => 0.3,
    audioEvents: (p, timing) => p.steps.map((_, i) => ({ beat: doneAt(i, p.steps.length, timing), sfx: 'tick' as const, gain: 0.7 })),
    post: (_p, env) => (env.style.background === 'dark' ? { bloom: 0.3, lens: 0 } : { bloom: 0.2, lens: 0 }),
    draw(ctx, props, env) {
        const tk = ui.tokens(env);
        const L = sceneBeats(env);
        const n = props.steps.length;
        ctx.fillStyle = tk.bg;
        ctx.fillRect(0, 0, DESIGN_W, DESIGN_H);
        if (tk.dark)
            kit.fx.pool(ctx, DESIGN_W / 2, DESIGN_H * 0.55, 1100, tk.accent, 0.06);
        kit.camera.zoom(ctx, 1 + 0.025 * kit.beat.seg(env, 0, L, kit.curves.silk));
        const done = props.steps.map((_, i) => doneAt(i, n, { beats: L }));
        const start = (i: number) => done[i]! - WORK;
        // Position along the steps: i while step i works, i + 1 once it is done.
        const b = env.localBeat;
        let at = 0;
        for (let i = 0; i < n; i++)
            if (b >= start(i))
                at = i + clamp((b - start(i)) / WORK);
        // --- The line through the markers draws on as steps complete ------------------------------------
        const marker = (i: number) => card(i, n).x + 40;
        const line = kit.beat.seg(env, 0, 0.8, kit.curves.glide);
        ctx.fillStyle = tk.track;
        ctx.fillRect(marker(0), MARK_Y - 2, (marker(n - 1) - marker(0)) * line, 4);
        for (let i = 0; i < n - 1; i++) {
            const k = kit.beat.seg(env, done[i]!, done[i]! + 0.6, kit.curves.glide);
            if (k <= 0)
                continue;
            ctx.fillStyle = tk.accent;
            ctx.fillRect(marker(i) + 32, MARK_Y - 2, (marker(i + 1) - marker(i) - 64) * k, 4);
        }
        props.steps.forEach((s, i) => {
            const c = card(i, n);
            const enter = kit.beat.seg(env, 0.15 + i * 0.25, 0.75 + i * 0.25, kit.curves.glide);
            if (enter <= 0)
                return;
            ctx.save();
            ctx.globalAlpha *= enter;
            ctx.translate(0, (1 - enter) * 40);
            // Marker: ordinal while waiting and working, a check once done.
            const fin = kit.beat.seg(env, done[i]!, done[i]! + 0.25);
            // Lights when its step starts working and stays lit.
            const lit = Math.max(fin, kit.beat.seg(env, start(i) - 0.2, start(i)));
            const mx = marker(i);
            const pulse = kit.beat.hits(env, [done[i]!], 7);
            ctx.beginPath();
            ctx.arc(mx, MARK_Y, 30 + 4 * pulse, 0, Math.PI * 2);
            ctx.fillStyle = kit.color.mix(tk.bg, tk.accent, fin);
            ctx.fill();
            ctx.lineWidth = 3;
            ctx.strokeStyle = kit.color.mix(tk.track, tk.accent, lit);
            ctx.stroke();
            if (fin < 1) {
                ctx.save();
                ctx.globalAlpha *= 1 - fin;
                ctx.font = tk.font(tk.type.body, 700);
                ui.label(ctx, String(i + 1), mx, MARK_Y + 1, kit.color.mix(tk.muted, tk.accentText, lit), 'center');
                ctx.restore();
            }
            if (fin > 0)
                ui.icon(ctx, 'check', mx - 17, MARK_Y - 17, 34, tk.onAccent, fin);
            // Card: the title, an optional line, and the interface that does the step.
            ui.panel(ctx, tk, c, { lift: 1 + 0.8 * clamp(1 - Math.abs(at - i - 0.5) * 2) });
            const pad = 36;
            ctx.font = tk.font(TITLE_PX, 700);
            // Wrap short of the action icon in the corner.
            const lines = kit.text.wrap(ctx, s.title, c.w - 2 * pad - 56);
            lines.forEach((l, j) => ui.label(ctx, l.text, c.x + pad, c.y + 64 + j * 50, tk.text));
            const below = c.y + 64 + (lines.length - 1) * 50 + 30;
            if (s.text && s.action !== 'type') {
                ctx.font = tk.font(tk.type.body, 500);
                ui.label(ctx, s.text, c.x + pad, below + 28, tk.muted);
            }
            ui.icon(ctx, ICON[s.action], c.x + c.w - pad - 40, c.y + 44, 40, tk.faint);
            const area: Box = { x: c.x + pad, y: below + 70, w: c.w - 2 * pad, h: c.y + c.h - pad - (below + 70) };
            action(ctx, tk, env, s, area, start(i), done[i]!);
            ctx.restore();
        });
    },
});
